import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

import { IntentResource } from "types/generated";

type GroupMeta = {
  isOpen: boolean;
  hasMore: boolean;
  endCursor?: string;
};

type FetchGroupChildrenResult = {
  resources: IntentResource[];
  hasMore: boolean;
  endCursor?: string;
};

type FetchGroupChildren = (props: {
  after?: string;
}) => Promise<FetchGroupChildrenResult | undefined>;

const DEFAULT_META: GroupMeta = {
  isOpen: false,
  hasMore: false,
  endCursor: undefined,
};

export type IntentProjectResourcesHierarchyStore = {
  childrenByGroup: Record<string, IntentResource[]>;
  groupMeta: Record<string, GroupMeta>;
  toggleGroup: (groupId: string) => void;
  loadGroupChildren: (groupId: string, fetchChildren: FetchGroupChildren) => Promise<void>;
  reset: () => void;
};

const useIntentProjectResourcesHierarchyStore = create<IntentProjectResourcesHierarchyStore>()(
  immer((set, get) => ({
    childrenByGroup: {},
    groupMeta: {},

    toggleGroup: (groupId) =>
      set((state) => {
        const meta = state.groupMeta[groupId] ?? DEFAULT_META;
        state.groupMeta[groupId] = { ...meta, isOpen: !meta.isOpen };
      }),

    loadGroupChildren: async (groupId, fetchChildren) => {
      const { endCursor } = get().groupMeta[groupId] ?? DEFAULT_META;
      const result = await fetchChildren({ after: endCursor });

      if (!result) {
        return undefined;
      }

      const { resources, hasMore, endCursor: nextCursor } = result;

      set((state) => {
        const prevChildren = state.childrenByGroup[groupId] ?? [];
        const prevMeta = state.groupMeta[groupId] ?? DEFAULT_META;

        state.childrenByGroup[groupId] = [...prevChildren, ...resources];
        state.groupMeta[groupId] = { ...prevMeta, hasMore, endCursor: nextCursor };
      });
    },

    reset: () => set({ childrenByGroup: {}, groupMeta: {} }),
  }))
);

export default useIntentProjectResourcesHierarchyStore;
